import { z } from 'zod';

/**
 * This doesn't contain a NEXT_PUBLIC prefix, so we can't really use it client-side, but because it's usually
 * the same, I'll just use this default and allow it to be overridden server-side if needed.
 */
const DEFAULT_NEXUDUS_API_BASE_URL = 'https://spaces.nexudus.com';
const DEFAULT_NEXT_PUBLIC_STORAGE_URL = 'https://storage.nexudus.com';
const DEFAULT_NEXUDUS_DASHBOARD_URL = 'https://dashboard.nexudus.com';
const DEFAULT_NEXUDUS_INTEGRATION_SANDBOX_BASE_URL =
  'https://sandbox.nexudus.com';

const nexudusEnvSchema = z.enum(['DEV', 'PROD', 'TEST']);

const urlOrPathSchema = z
  .string()
  .url()
  .or(z.string().regex(/^\/[a-zA-Z0-9_\-/]+(\/[a-zA-Z0-9_\-/]+)*$/)); // Must start with a slash

export const environmentVariables = {
  // WE have to explicitly mention each variable here so next knows to include them in the client bundle
  NEXT_PUBLIC_NEXUDUS_ENV: process.env.NEXT_PUBLIC_NEXUDUS_ENV,
  NEXUDUS_ENV_BASE_URL: process.env.NEXUDUS_ENV_BASE_URL, // https://bruntwoodsandboxnetwork.spaces.nexudus.com or https://bruntwoodnetwork.spaces.nexudus.com
  NEXT_PUBLIC_COMPANIES_HOUSE_API_KEY:
    process.env.NEXT_PUBLIC_COMPANIES_HOUSE_API_KEY,
  NEXT_PUBLIC_COMPANIES_HOUSE_API_URL:
    process.env.NEXT_PUBLIC_COMPANIES_HOUSE_API_URL,
  NEXT_PUBLIC_STORAGE_URL: process.env.NEXT_PUBLIC_STORAGE_URL,
  NEXT_PUBLIC_PORTAL_SERVICE_URL: process.env.NEXT_PUBLIC_PORTAL_SERVICE_URL,
  NEXT_PUBLIC_PORTAL_URL: process.env.NEXT_PUBLIC_PORTAL_URL,
  NEXUDUS_API_BASE_URL: process.env.NEXUDUS_API_BASE_URL,
  NEXUDUS_INTEGRATION_SANDBOX_BASE_URL:
    process.env.NEXUDUS_INTEGRATION_SANDBOX_BASE_URL,
  NEXT_PUBLIC_LOGROCKET_ID: process.env.NEXT_PUBLIC_LOGROCKET_ID,
  NEXT_PUBLIC_AUTH_BASE_URL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
  NEXT_PUBLIC_DASHBOARD_URL: process.env.NEXT_PUBLIC_DASHBOARD_URL,
  NEXT_PUBLIC_GYM_CLASSES_EVENT: process.env.NEXT_PUBLIC_GYM_CLASSES_EVENT,
  NEXT_PUBLIC_GTAG: process.env.NEXT_PUBLIC_GTAG,
  NEXT_PUBLIC_GA4: process.env.NEXT_PUBLIC_GA4,
  AUTH_SERVICE_PROXY_URL: process.env.AUTH_SERVICE_PROXY_URL,
  NEXT_PUBLIC_RENOLD_BUSINESS_ID: process.env.NEXT_PUBLIC_RENOLD_BUSINESS_ID,
  APPLICATIONINSIGHTS_CONNECTION_STRING:
    process.env.APPLICATIONINSIGHTS_CONNECTION_STRING,
  NEXT_PUBLIC_APPLICATION_INSIGHTS_CONNECTION_STRING:
    process.env.NEXT_PUBLIC_APPLICATION_INSIGHTS_CONNECTION_STRING,
  NEXT_PUBLIC_PACKAGE_VERSION: process.env.NEXT_PUBLIC_PACKAGE_VERSION,
  NEXT_PUBLIC_RELEASE_ID: process.env.NEXT_PUBLIC_RELEASE_ID,
  JWT_PUBLIC_KEY: process.env.JWT_PUBLIC_KEY,
  NEXT_PUBLIC_STRIPE_PUBLIC_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY,
  STRIPE_PRIVATE_KEY: process.env.STRIPE_PRIVATE_KEY,
  STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
  NEXUDUS_INTEGRATION_USERNAME: process.env.NEXUDUS_INTEGRATION_USERNAME,
  NEXUDUS_INTEGRATION_PASSWORD: process.env.NEXUDUS_INTEGRATION_PASSWORD,
  NEXUDUS_USERNAME: process.env.NEXUDUS_USERNAME,
  NEXUDUS_PASSWORD: process.env.NEXUDUS_PASSWORD,
  GA_ANALYTICS_SECRET_WEB: process.env.GA_ANALYTICS_SECRET_WEB,
  GA_ANALYTICS_SECRET_IOS: process.env.GA_ANALYTICS_SECRET_IOS,
  GA_ANALYTICS_SECRET_ANDROID: process.env.GA_ANALYTICS_SECRET_ANDROID,
  RLT_FORM_PAGE_ID: process.env.RLT_FORM_PAGE_ID,
  NEXT_PUBLIC_RLT_RESTRICTED_RESOURCE_IDS:
    process.env.NEXT_PUBLIC_RLT_RESTRICTED_RESOURCE_IDS,
  ALLOWED_FORM_IDS: process.env.ALLOWED_FORM_IDS,
} as const;

const DEFAULT_APPLE_PAY_MERCHANT_ID = 'merchant.com.bruntwood.stripe';
/**
 * This contains all the environment variables that can be used in client side code. Since we use the pages router, and
 * nearly every component can run both client and server
 */
const clientConfigObjectSchema = z.object({
  NEXT_PUBLIC_NEXUDUS_ENV: nexudusEnvSchema,
  NEXT_PUBLIC_COMPANIES_HOUSE_API_KEY: z.string().optional(),
  NEXT_PUBLIC_COMPANIES_HOUSE_API_URL: urlOrPathSchema.optional(),
  NEXT_PUBLIC_STORAGE_URL: urlOrPathSchema.default(
    DEFAULT_NEXT_PUBLIC_STORAGE_URL,
  ),
  NEXT_PUBLIC_PORTAL_SERVICE_URL: urlOrPathSchema,
  NEXT_PUBLIC_PORTAL_URL: z.string().url(),
  NEXUDUS_API_BASE_URL: urlOrPathSchema.default(DEFAULT_NEXUDUS_API_BASE_URL),
  NEXT_PUBLIC_LOGROCKET_ID: z.string().optional(),
  NEXT_PUBLIC_AUTH_BASE_URL: urlOrPathSchema,
  NEXT_PUBLIC_DASHBOARD_URL: urlOrPathSchema.default(
    DEFAULT_NEXUDUS_DASHBOARD_URL,
  ),
  NEXT_PUBLIC_GYM_CLASSES_EVENT: z.coerce.number().gt(0),
  NEXT_PUBLIC_GTAG: z.string(),
  NEXT_PUBLIC_GA4: z.string(),
  NEXT_PUBLIC_RENOLD_BUSINESS_ID: z.string(),
  NEXT_PUBLIC_APPLICATION_INSIGHTS_CONNECTION_STRING: z.string().optional(),
  NEXT_PUBLIC_PACKAGE_VERSION: z.string().optional(),
  NEXT_PUBLIC_RELEASE_ID: z.string().optional(),
  NEXT_PUBLIC_STRIPE_PUBLIC_KEY: z.string(),
  NEXT_PUBLIC_APPLE_PAY_MERCHANT_ID: z
    .string()
    .default(DEFAULT_APPLE_PAY_MERCHANT_ID),
  NEXT_PUBLIC_RLT_RESTRICTED_RESOURCE_IDS: z
    .string()
    .optional()
    .default('')
    .transform((value) =>
      value
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id)),
    ),
});

const clientConfigTransformer = (
  data: z.TypeOf<typeof clientConfigObjectSchema>,
) => ({
  isProd: data.NEXT_PUBLIC_NEXUDUS_ENV === 'PROD',
  isDev: data.NEXT_PUBLIC_NEXUDUS_ENV === 'DEV',
  isTest: data.NEXT_PUBLIC_NEXUDUS_ENV === 'TEST',
  chapi: {
    auth: data.NEXT_PUBLIC_COMPANIES_HOUSE_API_KEY,
    url: data.NEXT_PUBLIC_COMPANIES_HOUSE_API_URL,
  },
  storageUrl: data.NEXT_PUBLIC_STORAGE_URL,
  portalServiceRoot: data.NEXT_PUBLIC_PORTAL_SERVICE_URL,
  portalUrl: data.NEXT_PUBLIC_PORTAL_URL,
  platformRoot: data.NEXUDUS_API_BASE_URL,
  logRocketId: data.NEXT_PUBLIC_LOGROCKET_ID,
  authServiceUrl: data.NEXT_PUBLIC_AUTH_BASE_URL,
  gymEventId: data.NEXT_PUBLIC_GYM_CLASSES_EVENT,
  gtag: data.NEXT_PUBLIC_GTAG,
  ga4: data.NEXT_PUBLIC_GA4,
  renoldBusinessId: data.NEXT_PUBLIC_RENOLD_BUSINESS_ID,
  packageVersion: data.NEXT_PUBLIC_PACKAGE_VERSION,
  releaseId: data.NEXT_PUBLIC_RELEASE_ID,
  stripePublicKey: data.NEXT_PUBLIC_STRIPE_PUBLIC_KEY,
  applePayMerchantId: data.NEXT_PUBLIC_APPLE_PAY_MERCHANT_ID,
  rltRestrictedResourceIds: data.NEXT_PUBLIC_RLT_RESTRICTED_RESOURCE_IDS,
});

const clientConfigSchema = clientConfigObjectSchema.transform(
  clientConfigTransformer,
);

const serverConfigObjectSchema = z.intersection(
  clientConfigObjectSchema,
  z.object({
    NEXUDUS_ENV_BASE_URL: urlOrPathSchema,
    NEXUDUS_API_BASE_URL: urlOrPathSchema.default(DEFAULT_NEXUDUS_API_BASE_URL),
    NEXUDUS_INTEGRATION_SANDBOX_BASE_URL: urlOrPathSchema.default(
      DEFAULT_NEXUDUS_INTEGRATION_SANDBOX_BASE_URL,
    ),
    AUTH_SERVICE_PROXY_URL: urlOrPathSchema.optional(),
    APPLICATIONINSIGHTS_CONNECTION_STRING: z.string().optional(),
    JWT_PUBLIC_KEY: z.string().transform((key) => key.replace(/\\n/g, '\n')),
    STRIPE_PRIVATE_KEY: z.string(),
    STRIPE_WEBHOOK_SECRET: z.string(),
    NEXUDUS_USERNAME: z.string(),
    NEXUDUS_PASSWORD: z.string(),
    NEXUDUS_INTEGRATION_USERNAME: z.string().optional(),
    NEXUDUS_INTEGRATION_PASSWORD: z.string().optional(),
    GA_ANALYTICS_SECRET_WEB: z.string(),
    GA_ANALYTICS_SECRET_IOS: z.string(),
    GA_ANALYTICS_SECRET_ANDROID: z.string(),
    RLT_FORM_PAGE_ID: z.coerce.number().gt(0),
    ALLOWED_FORM_IDS: z.string().optional().default(''),
  }),
);

const serverConfigTransformer = (
  data: z.TypeOf<typeof serverConfigObjectSchema>,
) => ({
  ...clientConfigTransformer(data),
  nexudusEnvRoot: data.NEXUDUS_ENV_BASE_URL,
  nexudusDomain: new URL(data.NEXUDUS_ENV_BASE_URL).hostname.split('.')[0],
  nexudusApiRoot: data.NEXUDUS_API_BASE_URL,
  authServiceProxyUrl: data.AUTH_SERVICE_PROXY_URL,
  applicationInsightsConnectionString:
    data.APPLICATIONINSIGHTS_CONNECTION_STRING ||
    data.NEXT_PUBLIC_APPLICATION_INSIGHTS_CONNECTION_STRING,
  jwtPublicKey: data.JWT_PUBLIC_KEY,
  stripePrivateKey: data.STRIPE_PRIVATE_KEY,
  stripeWebhookSecret: data.STRIPE_WEBHOOK_SECRET,
  nexudusUsername: data.NEXUDUS_USERNAME,
  nexudusPassword: data.NEXUDUS_PASSWORD,
  nexudusIntegrationUsername: data.NEXUDUS_INTEGRATION_USERNAME,
  nexudusIntegrationPassword: data.NEXUDUS_INTEGRATION_PASSWORD,
  gaMeasurementId: data.NEXT_PUBLIC_GA4,
  gaApiSecretWeb: data.GA_ANALYTICS_SECRET_WEB,
  gaApiSecretIOS: data.GA_ANALYTICS_SECRET_IOS,
  gaApiSecretAndroid: data.GA_ANALYTICS_SECRET_ANDROID,
  rltFormPageId: data.RLT_FORM_PAGE_ID,
  allowedFormIds: data.ALLOWED_FORM_IDS,
});
const configSchema = serverConfigObjectSchema.transform(
  serverConfigTransformer,
);

export const clientConfig = () =>
  clientConfigSchema.parse(environmentVariables);

export const serverConfig = () => configSchema.parse(environmentVariables);

export type ServerConfig = ReturnType<typeof serverConfig>;

export const oktaConfig = {
  issuer: 'https://login.bruntwood.co.uk/oauth2/default',
  clientId: '0oa18ti6peuLRUXZu697',
};

export const resourceSlotTimes = {
  startHour: 8,
  startMin: 30,
  endHour: 17,
  endMin: 30,
};

export const renoldThemeColour = '#A97CFC';

const config = {
  serverConfig,
  clientConfig,
  oktaConfig,
  resourceSlotTimes,
  renoldThemeColour,
};
export default config;
